import {Checkbox, Form, Input, Space, Typography} from "@ui";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import GoogleSignIn from "@/components/Auth/GoogleSignIn";
import {PrimaryButton} from "@/components/Button";
import useGoogleLogin from "@/hooks/useGoogleLogin";
import {dropStorage, getStorage, PERSISTENT_COOKIE_OPTIONS, setStorage} from "@/store/util.store";
import eyeActive from "../../assets/images/icon-eye-active.svg";
import eyeInactive from "../../assets/images/icon-eye-inactive.svg";
import useLogin from "../../hooks/useLogin";
import {useAuthStore} from "../../store/useAuth.store";
import {useLogo} from "../../util/logoUtils";
import styles from "./MobileLogin.module.css";

const MobileLogin = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const logo = useLogo();
  const [form] = Form.useForm();
  const [rememberMe, setRememberMe] = useState(false);

  const authStatus = useAuthStore(state => state.authStatus);
  const validateSession = useAuthStore(state => state.validateSession);

  const {postLogin, isPending, isError} = useLogin();
  const {postGoogleLogin} = useGoogleLogin();

  useEffect(() => {
    // Validate session on component mount
    validateSession();

    // Mark that user is using mobile login flow
    setStorage("isMobileLoginUser", "true");
  }, [validateSession]);

  // Handle Google OAuth redirect: extract id_token from URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || !hash.includes("id_token=")) return;

    const params = new URLSearchParams(hash.substring(1));
    const idToken = params.get("id_token");

    // Clear the hash to avoid re-processing on re-render
    window.history.replaceState(null, "", window.location.pathname);

    if (idToken) {
      setStorage("isMobileLoginUser", "true");
      postGoogleLogin({idToken});
    }
  }, [postGoogleLogin]);

  useEffect(() => {
    const savedEmail = getStorage("rememberedEmail");
    const wasRemembered = getStorage("rememberMeChecked") === "true";

    if (savedEmail && wasRemembered) {
      form.setFieldsValue({username: savedEmail});
      setRememberMe(true);
    }
  }, [form]);

  useEffect(() => {
    // Redirect to appropriate page if user is authenticated
    if (authStatus === "authenticated") {
      const user = useAuthStore.getState().user;
      const authorizedEmails = ["test01@googletest.com", "test01@gmailtest.com"];

      if (user && authorizedEmails.includes(user.email.toLowerCase())) {
        // Special user should go to Lotoven
        navigate("/lotoven/home", {replace: true});
      } else {
        // Regular users go to home
        navigate("/", {replace: true});
      }
    }
  }, [authStatus, navigate]);

  const onFinish = values => {
    const trimmedUsername = values.username.trim();

    if (trimmedUsername === "testaccount@gmail.com") {
      // Handle test account error
      return;
    }

    // Check for special test user that needs immediate redirect (note: checking both possible emails)
    if (
      trimmedUsername.toLowerCase() === "test01@googletest.com" ||
      trimmedUsername.toLowerCase() === "test01@gmailtest.com"
    ) {
      // Handle remember me functionality first
      if (rememberMe) {
        setStorage("rememberedEmail", trimmedUsername, PERSISTENT_COOKIE_OPTIONS);
        setStorage("rememberMeChecked", "true", PERSISTENT_COOKIE_OPTIONS);
      } else {
        dropStorage("rememberedEmail");
        dropStorage("rememberMeChecked");
      }

      // For the special user, proceed with login first to get authenticated state
      // Then will redirect via the useEffect above after successful login
      setStorage("isMobileLoginUser", "true");

      postLogin({...values, username: trimmedUsername});
      return;
    }

    // Handle remember me functionality
    if (rememberMe) {
      setStorage("rememberedEmail", trimmedUsername, PERSISTENT_COOKIE_OPTIONS);
      setStorage("rememberMeChecked", "true", PERSISTENT_COOKIE_OPTIONS);
    } else {
      dropStorage("rememberedEmail");
      dropStorage("rememberMeChecked");
    }

    // Ensure mobile login flag persists through login
    setStorage("isMobileLoginUser", "true");

    postLogin({...values, username: trimmedUsername});
  };

  const handleRememberMeChange = e => {
    setRememberMe(e.target.checked);

    // If unchecking, immediately remove saved email
    if (!e.target.checked) {
      dropStorage("rememberedEmail");
      dropStorage("rememberMeChecked");
    }
  };

  const onFinishFailed = _errorInfo => {};

  const isValidEmail = email => {
    if (!email) {
      return {valid: false, message: t("registrationEmail_required")};
    }

    const specialCharRegex = /[´ñáéíóúáèìòùâêîôûäëïöüãõ¨';[(){}¡!"ªº·#$%^&*+=\\|~<>¿?]/;
    if (specialCharRegex.test(email)) {
      return {valid: false, message: t("registrationEmail_specialChar")};
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;
    if (!emailRegex.test(email)) {
      return {valid: false, message: t("registrationEmail_message")};
    }

    const [localPart, domainPart] = email.split("@");

    if (
      localPart.startsWith(".") ||
      localPart.endsWith(".") ||
      localPart.startsWith("-") ||
      localPart.startsWith("+") ||
      localPart.endsWith("-") ||
      localPart.endsWith("+")
    ) {
      return {valid: false, message: t("registrationEmail_CharStartEnd")};
    }

    if (
      localPart.includes("..") ||
      domainPart.includes("..") ||
      domainPart.includes("--") ||
      localPart.includes("--")
    ) {
      return {valid: false, message: t("registrationEmail_CharConsecutive")};
    }

    const domainParts = domainPart.split(".");
    for (const part of domainParts) {
      if (part.startsWith("-") || part.endsWith("-")) {
        return {valid: false, message: t("registrationEmail_CharStartEnd")};
      }
    }

    return {valid: true, message: ""};
  };

  // Show loading while checking authentication
  if (authStatus === "checking") {
    return (
      <div className={styles.loadingContainer}>
        <img
          src={logo}
          alt="logo"
          className={styles.loadingLogo}
        />
        <div className={styles.loadingSpinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.mobileLoginContainer}>
      <div className={styles.mobileLoginContent}>
        <div className={styles.logoContainer}>
          <img
            src={logo}
            alt="logo"
            className={styles.logo}
          />
        </div>

        <div className={styles.formContainer}>
          <Form
            form={form}
            name="mobile-login"
            className={styles.form}
            initialValues={{remember: true}}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            layout="vertical"
          >
            <div className={styles.formInner}>
              <h3 className={styles.title}>{t("loginSessionTitle")}</h3>

              <Form.Item
                name="username"
                rules={[
                  {
                    required: true,
                    message: t("loginEmail"),
                  },
                  {
                    validator: (_, value) => {
                      if (value) {
                        const trimmedValue = value.trim();
                        const result = isValidEmail(trimmedValue);
                        if (!result.valid) {
                          return Promise.reject(new Error(result.message));
                        }
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                style={{width: "100%", marginBottom: "20px"}}
              >
                <Input
                  placeholder={t("loginEmailPlaceholder")}
                  className={styles.input}
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    message: t("loginPassword"),
                  },
                ]}
                style={{width: "100%", marginBottom: "10px"}}
              >
                <Input.Password
                  placeholder={t("loginPasswordPlaceholder")}
                  className={styles.input}
                  iconRender={visible => (
                    <img
                      src={visible ? eyeActive : eyeInactive}
                      alt="toggle password"
                      style={{width: 24, height: 24}}
                    />
                  )}
                />
              </Form.Item>

              <div className={styles.rememberMeContainer}>
                <Checkbox
                  checked={rememberMe}
                  onChange={handleRememberMeChange}
                  className={styles.rememberMeCheckbox}
                >
                  <span className={styles.rememberMeText}>{t("loginRememberEmail") || "Recordar mi correo"}</span>
                </Checkbox>
              </div>

              <div className={styles.forgotPassword}>
                <Typography.Text
                  onClick={() => navigate("/mobile-password-recovery")}
                  className={styles.forgotPasswordLink}
                >
                  {t("loginForgotPassword")}
                </Typography.Text>
              </div>

              {isError && (
                <div className={styles.errorMessage}>
                  <Typography.Text style={{color: "var(--accent-danger)"}}>
                    {t("loginIncorrectPasswordEmail")}
                  </Typography.Text>
                </div>
              )}

              <Form.Item style={{textAlign: "center", marginBottom: "20px"}}>
                <PrimaryButton
                  htmlType="submit"
                  loading={isPending}
                  className={styles.submitButton}
                >
                  {t("loginSendBtn")}
                </PrimaryButton>
              </Form.Item>

              <Form.Item style={{textAlign: "center", marginBottom: "20px"}}>
                <GoogleSignIn shape="pill" />
              </Form.Item>

              <Form.Item style={{textAlign: "center", marginBottom: 0}}>
                <Space
                  size="middle"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography.Text className={styles.noAccountText}>
                    {t("loginNoAccount")}
                    <Typography.Link
                      className={styles.registerLink}
                      style={{color: "var(--action-primary)"}}
                      onClick={() => navigate("/mobile-registration")}
                    >
                      {t("loginRegister")}
                    </Typography.Link>
                  </Typography.Text>
                </Space>
              </Form.Item>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default MobileLogin;
